import { Map } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import CalendarPicker from "../../components/calendarPicker";
import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import FormDatePicker from "../../components/form/formDatePicker/FormDatePicker";
import FormDropdown from "../../components/form/FormDropdown";
import FormDurationPicker from "../../components/form/FormDurationPicker";
import FormIconPicker from "../../components/form/FormIconPicker";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorRow from "../../components/inspector/InspectorRow";
import Icon from "../../components/interface/Icon";
import CheckboxStates from "../../constants/checkboxStates";
import {
  DATE_FIELD_EARLIEST_END,
  DATE_FIELD_END,
  DATE_FIELD_LATEST_START,
  DATE_FIELD_START,
  DATE_FIELDS
} from "../../constants/dateFields";
import interfaceIdentifiers from "../../constants/interfaceIdentifiers";
import interfaceUserActions from "../../constants/interfaceUserActions";
import { NARRATIVE_LABEL_AUTOMATIC } from "../../constants/narrativeAutomaticLabel";
import { appStateOperations } from "../../modules/appState";
import { batchActions } from "../../modules/chainReducer";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import dateSubtractionFunctions from "../../modules/definition/calendar/dateFunctions/dateSubtractionFunctions";
import { isOngoingAllowedForCalendar } from "../../modules/definition/calendar/selectors";
import { narrativeSettingsSelectors } from "../../modules/definition/narrativeSettings";
import { typeSelectors } from "../../modules/definition/types";
import { itemOperations, itemSelectors } from "../../modules/items";
import utils from "../../modules/items/utils";
import { narrativeOperations } from "../../modules/narrative";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { immutableRecords, PropTypeShapes } from "../../types";
import { DateRecord } from "../../types/immutableRecords/model/data/DateRecord";
import { getDatesOnPrecisionChange } from "../../utils/calendarPicker";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { trackEvent } from "../../utils/trackAnalytics";
import userInputValidators from "../../utils/userInputValidators";
import renderNotAllowedBox from "./renderNotAllowedBox";
import selectors from "./selectors";

const isIos = preprocessorQuery.isIos();

const iconNames = [
  "markerOther",
  "flag",
  "star",
  "markerPresent",
  "cake",
  "markerClink",
  "heart",
  "markerFireworks",
  "markerLantern",
  "mortar",
  "award",
  "sapling",
  "sun",
  "moon",
  "crown",
  "swords",
  "scroll",
  "clock",
  "hourglass",
  "happy",
  "neutral",
  "unhappy"
];

const colorNames = ["iconMono"];

const iconOptions = () => {
  let options = [];
  iconNames.forEach(i => {
    colorNames.forEach(c => {
      options.push({ color: c, icon: i });
    });
  });
  return options;
};

const narrativeLabelOptions = narrativeLabels => {
  return [
    {
      label: "Automatic",
      value: NARRATIVE_LABEL_AUTOMATIC
    },
    ...narrativeLabels
      .map(labelObj => {
        return {
          label: labelObj.label,
          value: labelObj.id
        };
      })
      .toArray()
  ];
};

const renderDatesDiffNote = (calendar, item) => {
  const { startStr, endStr, durationStr } = utils.getActualDateStrings(calendar, item);
  return (
    <InspectorRow label="Child Range">
      <InspectorField>
        <span className="dateDiffNote">
          <span>{`${startStr} –`}</span>
          <span>{endStr}</span>
          <span>({durationStr})</span>
        </span>
      </InspectorField>
    </InspectorRow>
  );
};

/**
 *
 * @param {function} onChange
 * @param {List} itemIds
 */
const getOnChangeDateFunction = (onChange, itemIds) => date => onChange(itemIds.toArray(), date);

const getOnPreferredStartDateChangeFunction = (onChange, itemIds) => date => onChange(itemIds.toArray(), date);

const renderDatesBox = (
  useProps,
  isStartExpanded,
  onToggleStart,
  isEndExpanded,
  onToggleEnd,
  onPrecisionChange,
  selectedDateField,
  onSelectedDateFieldChange
) => {
  const {
    itemData,
    onOngoingChange,
    autoAdjustEndDate,
    onPreferredStartChange,
    onPreferredEndChange,
    onLatestStartChange,
    onEarliestEndChange,
    onBulkDateChange,
    typesById,
    onSetHasChildPopover
  } = useProps;
  const {
    preferredStartDate,
    latestStartDate,
    earliestEndDate,
    preferredEndDate,
    durationToEarliestEnd,
    preferredDuration,
    duration,
    isDateLocked,
    ongoing,
    id,
    type,hasDates
  } = itemData;
  const showActualDate = utils.arePreferredDatesDifferentFromActualDates(itemData);

  const itemType = typesById.get(type);
  const startLabel = typeSelectors.getTypeStartDisplay(itemType);
  const endLabel = typeSelectors.getTypeEndDisplay(itemType);

  const onLatestStartChangeInner = (itemIds, date) => {
    onLatestStartChange(itemIds, date);
    if (!isStartExpanded && isDateValid(date)) {
      onToggleStart();
    }
  };

  const onEarliestEndChangeInner = (itemIds, date) => {
    onEarliestEndChange(itemIds, date);
    if (!isEndExpanded && isDateValid(date)) {
      onToggleEnd();
    }
  };

  const onBulkDateChangeInner = (itemIds, dates) => {
    onBulkDateChange(itemIds, dates);
    if (!isStartExpanded && isDateValid(dates.latestStartDate)) {
      onToggleStart();
    }
    if (!isEndExpanded && isDateValid(dates.earliestEndDate)) {
      onToggleEnd();
    }
  };

  const getDatePicker = field => {
    const isEndDate = field.toLowerCase().includes("end");
    return (
      <FormDatePicker
        isDual={ongoing ? false : true}
        disabled={isDateLocked || (isEndDate && ongoing)}
        ongoing={ongoing}
        selectedDateField={field}
        startDate={preferredStartDate}
        latestStartDate={latestStartDate}
        earliestEndDate={earliestEndDate}
        endDate={preferredEndDate}
        durationToEarliestEnd={durationToEarliestEnd}
        duration={duration}
        autoAdjustEndDate={autoAdjustEndDate}
        calendar={useProps.calendar}
        onStartDateChange={getOnPreferredStartDateChangeFunction(onPreferredStartChange, useProps.itemIds)}
        onLatestStartChange={getOnChangeDateFunction(onLatestStartChangeInner, useProps.itemIds)}
        onEarliestEndChange={getOnChangeDateFunction(onEarliestEndChangeInner, useProps.itemIds)}
        onEndDateChange={getOnChangeDateFunction(onPreferredEndChange, useProps.itemIds)}
        onBulkDateChange={getOnChangeDateFunction(onBulkDateChangeInner, useProps.itemIds)}
        dateForNow={dateFunctions.dateForNow(useProps.calendar)}
        backText={getLabelWithUntitled(useProps.itemData.label)}
        startLabel={startLabel}
        endLabel={endLabel}
        onToggleCalendarPicker={onSetHasChildPopover}
      />
    );
  };

  const ongoingCheckbox = isOngoingAllowedForCalendar(useProps.calendar) ? (
    <FormCheckbox
      checkboxState={ongoing}
      label={"Ongoing"}
      onChange={state => {
        onOngoingChange([id], state);
      }}
      disabled={isDateLocked}
    />
  ) : (
    <div />
  );

  let groupRightIcon = null;
  let headingComponent = null;
  //#!if iOS !== true
  groupRightIcon = hasDates && isDateLocked ? "inspectorScrollLock" : "inspectorScrollUnlock";
  headingComponent = ongoingCheckbox;
  //#!endif

  const hasLatestStartDate = isDateValid(latestStartDate);
  const hasEarliestEndDate = isDateValid(earliestEndDate);

  return useProps.typeData.canHaveDate ? (
    <InspectorGroup
      className="inspectorPositionTabCalendarGroup"
      key="preferredDates"
      label={!isIos ? "Dates" : null}
      rightIcon={groupRightIcon}
      rightIconDisabled={!hasDates}
      rightIconTooltip={isDateLocked ? "Unlock Item Dates" : "Lock Item Dates"}
      onRightIconClick={
        hasDates
          ? () => {
            useProps.onSetIsDateLocked(useProps.itemIds.toArray(), !isDateLocked);
          }
          : null
      }
      headingComponents={headingComponent}
    >
      {showActualDate ? renderDatesDiffNote(useProps.calendar, itemData) : null}

      {!isIos ? (
        <Fragment>
          {/* TODO: label when showing actual Date*/}
          {/* <InspectorRow label={`${showActualDate ? "Latest " : ""}Start`}> */}
          <InspectorRow label={`${isStartExpanded ? "Earliest " : ""}${startLabel}`}>
            <InspectorField>{getDatePicker(DATE_FIELD_START)}</InspectorField>
            {!isIos ? (
              <Icon
                icon={isStartExpanded ? "" : "downArrow"}
                className="collapseArrow"
                color="iconMono"
                onClick={isStartExpanded ? null : onToggleStart}
                tooltip={isStartExpanded ? null : "Toggle latest possible start date"}
              />
            ) : null}
          </InspectorRow>
          {isStartExpanded ? (
            <InspectorRow label={`Latest ${startLabel}`}>
              <InspectorField>{getDatePicker(DATE_FIELD_LATEST_START)}</InspectorField>
              {!isIos ? (
                <Icon
                  icon={hasLatestStartDate ? "closeWindow" : "upArrow"}
                  tooltip={hasLatestStartDate ? "Remove latest possible start date" : null}
                  className={`collapseArrow${isDateLocked && hasLatestStartDate ? " disabled" : ""}`}
                  color="iconMono"
                  onClick={!hasLatestStartDate || !isDateLocked ? onToggleStart : null}
                />
              ) : null}
            </InspectorRow>
          ) : null}
          {isEndExpanded ? (
            <InspectorRow label={`Earliest ${endLabel}`}>
              <InspectorField>{getDatePicker(DATE_FIELD_EARLIEST_END)}</InspectorField>
              {!isIos ? (
                <Icon
                  icon={hasEarliestEndDate ? "closeWindow" : ""}
                  tooltip={hasEarliestEndDate ? "Remove earliest possible end date" : null}
                  className={`collapseArrow${isDateLocked && hasEarliestEndDate ? " disabled" : ""}`}
                  color="iconMono"
                  onClick={hasEarliestEndDate && !isDateLocked ? onToggleEnd : null}
                />
              ) : null}
            </InspectorRow>
          ) : null}
          {/* TODO: label when showing actual Date*/}
          {/* // <InspectorRow label={`${showActualDate ? "Earliest " : ""}End`}> */}
          <InspectorRow label={`${isEndExpanded ? "Latest " : ""}${endLabel}`}>
            <InspectorField>{getDatePicker(DATE_FIELD_END)}</InspectorField>
            {!isIos ? (
              <Icon
                icon={isEndExpanded ? (hasEarliestEndDate ? "" : "upArrow") : "downArrow"}
                tooltip={isEndExpanded ? null : "Toggle earliest possible end date"}
                className="collapseArrow"
                color="iconMono"
                onClick={(isEndExpanded && !hasEarliestEndDate) || !isEndExpanded ? onToggleEnd : null}
              />
            ) : null}
          </InspectorRow>
        </Fragment>
      ) : (
        <InspectorRow>
          <InspectorField>
            <div className="calendarPickerChildren">
              <CalendarPicker
                startDate={preferredStartDate}
                latestStartDate={latestStartDate}
                earliestEndDate={earliestEndDate}
                endDate={preferredEndDate}
                durationToEarliestEnd={durationToEarliestEnd}
                duration={duration}
                allowUncertainDates={true}
                isDual={true}
                ongoing={ongoing}
                calendar={useProps.calendar}
                onStartDateChange={getOnPreferredStartDateChangeFunction(onPreferredStartChange, useProps.itemIds)}
                onLatestStartChange={getOnChangeDateFunction(onLatestStartChangeInner, useProps.itemIds)}
                onEarliestEndChange={getOnChangeDateFunction(onEarliestEndChangeInner, useProps.itemIds)}
                onEndDateChange={getOnChangeDateFunction(onPreferredEndChange, useProps.itemIds)}
                onPrecisionChange={onPrecisionChange}
                dateForNow={dateFunctions.dateForNow(useProps.calendar)}
                selectedDateField={selectedDateField}
                startLabel={startLabel}
                endLabel={endLabel}
                isDateLocked={isDateLocked}
                onFocusField={onSelectedDateFieldChange}
              />
              <br />
            </div>
          </InspectorField>
        </InspectorRow>
      )}

      <InspectorRow label="Duration">
        <InspectorField>
          <FormDurationPicker
            disabled={isDateLocked || ongoing || isStartExpanded || isEndExpanded}
            value={preferredDuration}
            calendar={useProps.calendar}
            onChange={span => useProps.onPreferredDurationChange(useProps.itemIds.toArray(), span)}
            validator={userInputValidators.itemDuration}
          />
        </InspectorField>
      </InspectorRow>
      {isIos ? (
        <InspectorRow>
          <InspectorField>{ongoingCheckbox}</InspectorField>
        </InspectorRow>
      ) : null}
      {isIos ? (
        <InspectorRow>
          <InspectorField>
            <FormCheckbox
              checkboxState={isDateLocked}
              label={"Lock Dates"}
              disabled={!hasDates}
              onChange={value => useProps.onSetIsDateLocked(useProps.itemIds.toArray(), value)}
            />
          </InspectorField>
        </InspectorRow>
      ) : null}
    </InspectorGroup>
  ) : isIos ? null : (
    renderNotAllowedBox("Dates", "dates", useProps.typeData.label, useProps.onOpenSettingsClick)
  );
};

const renderNarrativeBox = useProps => {
  const canIncludeInNarrative = useProps.typeData.canIncludeInNarrative;
  // isInNarrative is true if all selected items are in the narrative, false if all selected items are not, and null if there is a mix
  const isInNarrative = useProps.itemData.isInNarrative;

  // If can't be in narrative, do not render in iOS inspector
  if (!canIncludeInNarrative) {
    if (isIos) {
      return null;
    }
    return renderNotAllowedBox(
      "Narrative",
      "a narrative position",
      useProps.typeData.label,
      useProps.onOpenSettingsClick
    );
  }

  let includeInNarrativeCheckbox = null;
  if (isIos) {
    includeInNarrativeCheckbox = (
      <InspectorRow>
        <InspectorField>
          <FormCheckbox
            checkboxState={
              isInNarrative === true
                ? CheckboxStates.CHECKED
                : isInNarrative === false
                  ? CheckboxStates.UNCHECKED
                  : CheckboxStates.MIXED
            }
            label={"Include in Narrative"}
            onChange={value => useProps.onNarrativeToggle(useProps.itemIds.toArray(), value)}
          />
        </InspectorField>
      </InspectorRow>
    );
  }

  return (
    <InspectorGroup label="Narrative">
      {includeInNarrativeCheckbox}
      {!isIos && isInNarrative === false ? (
        <InspectorRow>
          <FormButton
            label={"Add to end of Narrative"}
            linkStyle={true}
            onClick={() => useProps.onNarrativeToggle(useProps.itemIds.toArray(), true)}
          />
        </InspectorRow>
      ) : null}
      {isInNarrative === true ? (
        <Fragment>
          <InspectorRow label="Section Type" verticalCenter={!isIos}>
            <InspectorField>
              <FormDropdown
                options={narrativeLabelOptions(useProps.narrativeLabels)}
                noneLabel="[Multiple]"
                value={useProps.itemData.narrativeLabel}
                onChange={labelId => useProps.onNarrativeLabelChange(useProps.itemIds.toArray(), labelId)}
                title={"Narrative Position"}
                backText={getLabelWithUntitled(useProps.itemData.label)}
                pulse={useProps.pulsateIconId === interfaceIdentifiers.INSPECTOR_NARRATIVE_SECTION_TYPE}
              />
            </InspectorField>
          </InspectorRow>
          {useProps.itemData.narrativePathLabel ? (
            <InspectorRow label="Position" verticalCenter={!isIos}>
              <InspectorField>
                <span>{useProps.itemData.narrativePathLabel}</span>
              </InspectorField>
            </InspectorRow>
          ) : null}
          {!isIos ? (
            <InspectorRow>
              <FormButton
                label={"Remove from Narrative"}
                linkStyle={true}
                onClick={() => useProps.onNarrativeClear(useProps.itemIds.toArray())}
              />
            </InspectorRow>
          ) : null}
        </Fragment>
      ) : null}
      {!isIos && _.isNil(isInNarrative) ? (
        <Fragment>
          <InspectorRow>
            <FormButton
              label={"Add missing to end of Narrative"}
              linkStyle={true}
              onClick={() => useProps.onNarrativeToggle(useProps.itemIds.toArray(), true)}
            />
          </InspectorRow>
          <InspectorRow>
            <FormButton
              label={"Remove all from Narrative"}
              linkStyle={true}
              onClick={() => useProps.onNarrativeClear(useProps.itemIds.toArray())}
            />
          </InspectorRow>
        </Fragment>
      ) : null}
    </InspectorGroup>
  );
};

const renderAnniversaryBox = useProps => {
  const itemType = useProps.typesById.get(useProps.itemData.type);
  const anniversaryLabel = typeSelectors.getTypeAnniversaryDisplay(itemType);
  const hasDate = isDateValid(useProps.itemData.startDate);
  const displayIcon = useProps.itemData.anniversaryIcon;

  const tooShortForAnniversary =
    useProps.itemData.showAnniversary &&
    !itemType.extendAnniversaryBeyondEnd &&
    !useProps.itemData.ongoing &&
    useProps.itemData.duration.years < 1;
  const tooShortForAnniversaryOngoing =
    !hasDate ||
    (useProps.itemData.showAnniversary &&
      !itemType.extendAnniversaryBeyondEnd &&
      useProps.itemData.ongoing &&
      dateSubtractionFunctions.timespanBetweenDates(
        useProps.calendar,
        useProps.itemData.startDate,
        dateFunctions.dateForNow(useProps.calendar)
      ).years < 1);
  return useProps.typeData.canHaveDate ? (
    <InspectorGroup
      showCheckbox={true}
      disabled={!hasDate && !useProps.itemData.showAnniversary}
      isChecked={useProps.itemData.showAnniversary}
      label={`Mark ${anniversaryLabel} on Timeline`}
      onChange={newState => useProps.onShowAnniversaryChange(useProps.itemIds.toArray(), newState)}
    >
      {tooShortForAnniversary || tooShortForAnniversaryOngoing ? (
        <div className="groupUnavailable">Not shown if duration is less than one year.</div>
      ) : null}
      <InspectorRow label="Icon">
        <FormIconPicker
          value={{ icon: displayIcon, color: "iconMono" }}
          options={iconOptions()}
          id="markerInspectorIconPicker"
          onChange={value => useProps.onAnniversaryIconChange(useProps.itemIds.toArray(), value.icon)}
          title={"Anniversary Icon"}
          backText={getLabelWithUntitled(useProps.itemData.label)}
        />
      </InspectorRow>
    </InspectorGroup>
  ) : null;
};

const getIsDateFieldExpanded = (itemData, dateKey) => itemData && itemData[dateKey] && isDateValid(itemData[dateKey]);

const getDatesRecordFromProps = useProps => {
  return immutableRecords.ItemDatesRecord({
    startDate: useProps.startDate,
    preferredStartDate: useProps.startDate,
    duration: useProps.duration,
    preferredDuration: useProps.duration,
    endDate: useProps.endDate,
    preferredEndDate: useProps.endDate,
    latestStartDate: useProps.latestStartDate,
    earliestEndDate: useProps.earliestEndDate,
    durationToEarliestEnd: useProps.durationToEarliestEnd
  });
};

class InspectorPositionTab extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isStartExpanded: getIsDateFieldExpanded(props.itemData, "latestStartDate"),
      isEndExpanded: getIsDateFieldExpanded(props.itemData, "earliestEndDate"),
      selectedDateField: DATE_FIELD_START
    };

    this.toggleStartExpanded = this.toggleStartExpanded.bind(this);
    this.toggleEndExpanded = this.toggleEndExpanded.bind(this);
    this.onPrecisionChange = this.onPrecisionChange.bind(this);
    this.onSelectedDateFieldChange = this.onSelectedDateFieldChange.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    const { latestStartDate: nextLatestStart, earliestEndDate: nextEarliestEnd } = nextProps.itemData;
    const { latestStartDate, earliestEndDate } = this.props.itemData;
    if (!dateFunctions.areDatesSame(nextLatestStart, latestStartDate)) {
      this.setState({ isStartExpanded: getIsDateFieldExpanded(nextProps.itemData, "latestStartDate") });
    }
    if (!dateFunctions.areDatesSame(nextEarliestEnd, earliestEndDate)) {
      this.setState({ isEndExpanded: getIsDateFieldExpanded(nextProps.itemData, "earliestEndDate") });
    }
  }

  toggleStartExpanded() {
    const { isStartExpanded } = this.state;
    const { onLatestStartChange, itemIds } = this.props;
    this.setState({ isStartExpanded: !isStartExpanded });
    if (isStartExpanded) {
      const emptyDate = DateRecord();
      onLatestStartChange(itemIds.toArray(), emptyDate);
    }
  }

  toggleEndExpanded() {
    const { isEndExpanded } = this.state;
    const { onEarliestEndChange, itemIds } = this.props;
    this.setState({ isEndExpanded: !isEndExpanded });
    if (isEndExpanded) {
      const emptyDate = DateRecord();
      onEarliestEndChange(itemIds.toArray(), emptyDate);
    }
  }

  onPrecisionChange(newPrecision, changeEndDates) {
    const dates = getDatesRecordFromProps(this.props.itemData);
    const datesOnPrecisionChange = getDatesOnPrecisionChange(
      newPrecision,
      changeEndDates,
      this.props.calendar,
      dates,
      this.props.autoAdjustEndDate
    );

    const datesChanged = datesOnPrecisionChange.datesChanged;

    if (datesChanged) {
      const calendarPickerDates = datesOnPrecisionChange.calendarPickerDates;
      const newDateValues = {
        startDate: calendarPickerDates.preferredStartDate,
        endDate: calendarPickerDates.preferredEndDate,
        latestStartDate: calendarPickerDates.latestStartDate,
        earliestEndDate: calendarPickerDates.earliestEndDate
      };
      const keys = DATE_FIELDS;
      keys.forEach(k => {
        newDateValues[k] = {
          timestamp: newDateValues[k] ? newDateValues[k].timestamp : null,
          precision: newDateValues[k] ? newDateValues[k].precision : null
        };
      });

      this.props.onBulkDateChange(this.props.itemIds, newDateValues);
    }
  }

  onSelectedDateFieldChange(dateField) {
    this.setState({ selectedDateField: dateField });
  }

  render() {
    const { includeNarrative } = this.props;
    const { isStartExpanded, isEndExpanded } = this.state;

    return (
      <Fragment>
        {renderDatesBox(
          this.props,
          isStartExpanded,
          this.toggleStartExpanded,
          isEndExpanded,
          this.toggleEndExpanded,
          this.onPrecisionChange,
          this.state.selectedDateField,
          this.onSelectedDateFieldChange
        )}
        {isIos ? null : renderAnniversaryBox(this.props)}
        {includeNarrative ? renderNarrativeBox(this.props) : null}
      </Fragment>
    );
  }
}

InspectorPositionTab.propTypes = {
  onPreferredStartChange: PropTypes.func.isRequired,
  onLatestStartChange: PropTypes.func.isRequired,
  onEarliestEndChange: PropTypes.func.isRequired,
  onPreferredEndChange: PropTypes.func.isRequired,
  onPreferredDurationChange: PropTypes.func.isRequired,
  onOngoingChange: PropTypes.func.isRequired,
  onSetIsDateLocked: PropTypes.func.isRequired,
  onShowAnniversaryChange: PropTypes.func.isRequired,
  onAnniversaryIconChange: PropTypes.func.isRequired,
  onOpenSettingsClick: PropTypes.func.isRequired,
  onSetHasChildPopover: PropTypes.func,
  itemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  itemData: PropTypeShapes.inspectorItem,
  typeData: PropTypeShapes.denormalisedTypeDefinition,
  dependencyOptions: PropTypeShapes.listOptionList,
  includeNarrative: PropTypes.bool.isRequired,
  narrativeLabels: ImmutablePropTypes.list,
  itemsById: ImmutablePropTypes.map.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  autoAdjustEndDate: PropTypes.bool.isRequired,
  onBulkDateChange: PropTypes.func,
  pulsateIconId: PropTypes.string
};

const mapStateToProps = (state, props) => {
  return {
    includeNarrative: definitionSelectors.isNarrativeAllowed(state.present),
    autoAdjustEndDate: definitionSelectors.getAdjustItemEndWhenChangingStart(state.present),
    narrativePositionsById: itemSelectors.getNarrativePositionsForItems(
      state.present,
      Map(props.itemIds.map(item => [item, item]))
    ),
    calendar: calendarSelectors.getCalendar(state.present),
    dependencyOptions: selectors.getDependencyOptions(state.present, props.itemIds),
    narrativeLabels: narrativeSettingsSelectors.getNarrativeLabels(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    pulsateIconId: uiStateSelectors.getPulsateIconId(state.present)
  };
};

const readonlyActions = {
  onOpenSettingsClick: uiStateOperations.showSettingsModal
};

const editingActions = {
  onPreferredStartChange: (itemIds, date) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "start");
    const actions = [itemOperations.setPreferredStartDate(itemIds, date.timestamp, date.precision)];
    actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.SET_START_DATE_INSPECTOR));
    return batchActions(actions);
  },
  onLatestStartChange: (itemIds, date) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "latestStart");
    return itemOperations.setLatestStartDate(itemIds, date.timestamp, date.precision);
  },
  onEarliestEndChange: (itemIds, date) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "earliestEnd");
    return itemOperations.setEarliestEndDate(itemIds, date.timestamp, date.precision);
  },
  onPreferredEndChange: (itemIds, date) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "end");
    return itemOperations.setPreferredEndDate(itemIds, date.timestamp, date.precision);
  },
  onBulkDateChange: (itemIds, values) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "bulkDates");
    const actions = [itemOperations.setBulkPreferredDates(itemIds, values)];
    actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.SET_START_DATE_INSPECTOR));
    return batchActions(actions);
  },
  onPreferredDurationChange: (itemIds, span) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "duration");
    return itemOperations.setPreferredDuration(itemIds, span ? span.toObject() : span);
  },
  onOngoingChange: (itemIds, value) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "ongoing");
    return itemOperations.setOngoing(itemIds, value);
  },
  onSetIsDateLocked: (itemIds, value) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "dateLock");
    return itemOperations.setIsDateLocked(itemIds, value);
  },
  onShowAnniversaryChange: (itemIds, show) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "showAnniversary");
    return itemOperations.setShowAnniversary(itemIds, show);
  },
  onAnniversaryIconChange: (itemIds, icon) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "anniversaryIcon");
    return itemOperations.setAnniversaryIcon(itemIds, icon);
  },
  onNarrativeLabelChange: (itemIds, labelId) => {
    trackEvent("setItemValueFromInspector", "setItemValue", "narrativeLabel");
    const actions = [itemOperations.setNarrativeLabel(itemIds, labelId)];
    actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.SET_NARRATIVE_INSPECTOR));
    return batchActions(actions);
  },
  onNarrativeClear: itemIds => {
    trackEvent("setItemValueFromInspector", "setItemValue", "narrativeClear");
    return narrativeOperations.removeFromNarrative(itemIds);
  },
  onNarrativeToggle: (itemIds, state) => {
    const result = state
      ? narrativeOperations.addToEndOfNarrative(itemIds)
      : narrativeOperations.removeFromNarrative(itemIds);
    return result;
  },
  onSetHasChildPopover: uiStateOperations.setLeftPanelHasChildPopover
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InspectorPositionTab);
